import React, { useState } from 'react';
import {
  IonButton,
  IonIcon,
  IonItem,
  IonLabel,
  IonSpinner,
  IonText,
  IonTextarea,
} from '@ionic/react';
import { volumeHigh } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { IvrFlowObject } from '../interfaces/ivr-configuration.interface';
import IvrConfigService from '../services/ivr-config.service';

interface MessageEditorProps {
  messageObject: IvrFlowObject;
  organizationId: string;
  onUpdate: (updatedObject: IvrFlowObject) => void;
  onAudioRegenerationNeeded?: (objectId: string, needed: boolean) => void;
}

const VOICE_OPTIONS = [
  { value: 'pt-BR-Standard-A', label: 'Standard Female (A)' },
  { value: 'pt-BR-Standard-B', label: 'Standard Male (B)' },
  { value: 'pt-BR-Wavenet-A', label: 'Wavenet Female (A)' },
  { value: 'pt-BR-Wavenet-B', label: 'Wavenet Female (B)' },
  { value: 'pt-BR-Wavenet-C', label: 'Wavenet Male (C)' },
];

const MessageEditor: React.FC<MessageEditorProps> = ({
  messageObject,
  organizationId,
  onUpdate,
  onAudioRegenerationNeeded,
}) => {
  const { t } = useTranslation();
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localTtsText, setLocalTtsText] = useState(messageObject.tts || '');
  const [textWhenAudioGenerated, setTextWhenAudioGenerated] = useState(messageObject.tts || '');

  const langId = messageObject.lang_id || 'pt-br';
  const ssmlGender = messageObject.ssml_gender || 'FEMALE';
  const voiceName = messageObject.voice_name || 'pt-BR-Standard-A';
  const audioUrl = messageObject.audio_Url || '';

  // Sync local state when messageObject changes (e.g., when switching between steps)
  React.useEffect(() => {
    setLocalTtsText(messageObject.tts || '');
    setTextWhenAudioGenerated(messageObject.tts || '');
  }, [messageObject.id]);

  // Check if audio regeneration is needed
  const needsAudioRegeneration = localTtsText.trim() !== textWhenAudioGenerated.trim() && audioUrl !== '';

  // Notify parent when audio regeneration state changes
  React.useEffect(() => {
    if (onAudioRegenerationNeeded) {
      onAudioRegenerationNeeded(String(messageObject.id), needsAudioRegeneration);
    }
  }, [needsAudioRegeneration, messageObject.id, onAudioRegenerationNeeded]);

  const handleTextChange = (value: string | null | undefined) => {
    setLocalTtsText(value || '');
  };

  const handleTextBlur = () => {
    if (localTtsText !== messageObject.tts) {
      onUpdate({
        ...messageObject,
        tts: localTtsText,
      });
    }
  };

  const handleVoiceChange = (field: string, value: string) => {
    onUpdate({
      ...messageObject,
      [field]: value,
    });
  };

  const handleGenerateAudio = async () => {
    if (!localTtsText.trim()) {
      setError('Please enter text to generate audio');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const response = await IvrConfigService.synthesizeTts(organizationId, {
        lang_id: langId,
        text: localTtsText,
        ssml_gender: ssmlGender,
        voice_name: voiceName,
      });

      // Update the text that audio was generated for
      setTextWhenAudioGenerated(localTtsText);
      
      onUpdate({
        ...messageObject,
        audio_Url: response.file_path,
        tts: localTtsText,
      });
    } catch (err) {
      setError(t('FAILED_TO_GENERATE_AUDIO'));
      console.error('TTS generation error:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div style={{ textAlign: 'left' }}>
      <IonText>
        <h3 style={{ fontSize: '16px', margin: '0 0 12px 0' }}>
          {t('MESSAGE_CONFIGURATION')}
          {messageObject.subtype && ` (${t(messageObject.subtype.toUpperCase())})`}
        </h3>
      </IonText>
      
      <IonItem>
        <IonLabel position="floating">{t('MESSAGE_TEXT')}</IonLabel>
        <IonTextarea
          rows={3}
          value={localTtsText}
          onIonChange={(e) => handleTextChange(e.detail.value)}
          onIonBlur={handleTextBlur}
          placeholder={t('ENTER_MESSAGE_TEXT')}
        />
      </IonItem>

      <IonButton
        expand="block"
        onClick={handleGenerateAudio}
        disabled={isGenerating || !localTtsText.trim()}
        style={{ marginTop: '12px' }}
        size="default"
      >
        {isGenerating ? (
          <>
            <IonSpinner name="crescent" />
            <span style={{ marginLeft: '8px' }}>{t('GENERATING')}</span>
          </>
        ) : (
          <>
            <IonIcon icon={volumeHigh} slot="start" />
            {t('GENERATE_AUDIO')}
          </>
        )}
      </IonButton>

      {error && (
        <IonText color="danger">
          <p style={{ marginTop: '8px', fontSize: '13px' }}>{error}</p>
        </IonText>
      )}

      {audioUrl && (
        <div style={{ marginTop: '12px' }}>
          <IonLabel style={{ fontSize: '13px' }}>
            <strong>{t('AUDIO_PREVIEW')}</strong>
          </IonLabel>
          <audio 
            key={audioUrl}
            controls 
            style={{ width: '100%', marginTop: '8px', height: '40px' }}
            src={audioUrl}
          >
            Your browser does not support the audio element.
          </audio>
        </div>
      )}
    </div>
  );
};

export default MessageEditor;
